import type { Catalogue, CatalogueDriver } from '@quoting/ladder'
import { useState } from 'react'
import { ToggleChip as Chip, cx, Field, Tag } from '../../components/ui'

const BLOCKS = [
  { key: 'market', name: 'Market situation' },
  { key: 'structure', name: 'Structural position' },
  { key: 'client', name: 'Client context' },
  { key: 'deal', name: 'Deal context' },
] as const

const GRAIN_FOR_BLOCK: Record<string, CatalogueDriver['grain']> = { market: 'market', structure: 'product', client: 'customer', deal: 'deal' }

export const EMPTY_CATALOGUE: Catalogue = { blocks: BLOCKS.map((b) => ({ key: b.key, name: b.name })), drivers: [] }

const slugId = (name: string) =>
  name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '')
    .slice(0, 40)

/**
 * Structured editor for a tenant's driver catalogue: blocks are fixed by the spec, drivers are
 * added per block with id, name, grain, kind, level count and the meaning of the two ends.
 */
export function CatalogueEditor({ value, onChange, disabled }: { value: Catalogue; onChange: (c: Catalogue) => void; disabled?: boolean }) {
  const [json, setJson] = useState<string | null>(null)
  const [jsonError, setJsonError] = useState<string | null>(null)

  const setDriver = (i: number, patch: Partial<CatalogueDriver>) =>
    onChange({ ...value, drivers: value.drivers.map((d, j) => (j === i ? { ...d, ...patch } : d)) })
  const removeDriver = (i: number) => onChange({ ...value, drivers: value.drivers.filter((_, j) => j !== i) })
  const addDriver = (block: CatalogueDriver['block']) =>
    onChange({
      ...value,
      drivers: [...value.drivers, { id: '', name: '', block, kind: 'relative', grain: GRAIN_FOR_BLOCK[block]!, levels: ['1', '2', '3', '4', '5'], anchorLow: '', anchorHigh: '', fitted: block === 'structure' || block === 'client' }],
    })
  const setBlockName = (key: string, name: string) => onChange({ ...value, blocks: value.blocks.map((b) => (b.key === key ? { ...b, name } : b)) })

  const applyJson = () => {
    if (json === null) return
    try {
      const parsed = JSON.parse(json) as Catalogue
      onChange(parsed)
      setJson(null)
      setJsonError(null)
    } catch (e) {
      setJsonError((e as Error).message)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="label">Blocks are fixed by the model</span>
        <span className="ml-auto" />
        {json === null ? (
          <button type="button" className="btn !py-0.5 text-xs" disabled={disabled} onClick={() => setJson(JSON.stringify(value, null, 2))}>
            Edit as JSON
          </button>
        ) : (
          <>
            <button type="button" className="btn btn-primary !py-0.5 text-xs" onClick={applyJson}>Apply JSON</button>
            <button type="button" className="btn !py-0.5 text-xs" onClick={() => { setJson(null); setJsonError(null) }}>Cancel</button>
          </>
        )}
      </div>
      {json !== null ? (
        <div>
          <textarea className={cx('field font-mono text-xs', jsonError && 'border-rose-500')} rows={18} value={json} onChange={(e) => setJson(e.target.value)} />
          {jsonError && <p className="mt-1 text-xs text-rose-300">{jsonError}</p>}
        </div>
      ) : (
        BLOCKS.map((b) => {
          const block = value.blocks.find((x) => x.key === b.key) ?? { key: b.key, name: b.name }
          const drivers = value.drivers.map((d, i) => ({ d, i })).filter(({ d }) => d.block === b.key)
          return (
            <div key={b.key} className="rounded-md border border-slate-800 bg-slate-900/40 p-3">
              <div className="mb-2 flex items-center gap-3">
                <Tag tone="info">{b.key}</Tag>
                <input className="field max-w-xs !py-1 text-sm font-semibold" disabled={disabled} value={block.name} onChange={(e) => setBlockName(b.key, e.target.value)} />
                <span className="text-xs text-slate-500">{drivers.length} driver{drivers.length === 1 ? '' : 's'} · grain {GRAIN_FOR_BLOCK[b.key]}</span>
                <button type="button" className="btn ml-auto !py-0.5 text-xs" disabled={disabled} onClick={() => addDriver(b.key)}>
                  Add driver
                </button>
              </div>
              {drivers.length === 0 && <p className="text-xs text-slate-600">No drivers in this block.</p>}
              <div className="space-y-2">
                {drivers.map(({ d, i }) => (
                  <div key={i} className="rounded-md border border-slate-800/70 p-2">
                    <div className="grid gap-2 md:grid-cols-12">
                      <div className="md:col-span-4">
                        <Field label="Name">
                          <input
                            className="field !py-1"
                            disabled={disabled}
                            value={d.name}
                            onChange={(e) => setDriver(i, { name: e.target.value, ...(d.id === '' || d.id === slugId(d.name) ? { id: slugId(e.target.value) } : {}) })}
                          />
                        </Field>
                      </div>
                      <div className="md:col-span-2">
                        <Field label="Id" hint="stable; used by ratings files">
                          <input className={cx('field !py-1 font-mono text-xs', !/^[a-z][a-z0-9_]{1,40}$/.test(d.id) && 'border-rose-500')} disabled={disabled} value={d.id} onChange={(e) => setDriver(i, { id: e.target.value })} />
                        </Field>
                      </div>
                      <div className="md:col-span-2">
                        <Field label="Grain">
                          <select className="field !py-1" disabled={disabled} value={d.grain} onChange={(e) => setDriver(i, { grain: e.target.value as CatalogueDriver['grain'] })}>
                            <option value="product">product</option>
                            <option value="customer">customer</option>
                            <option value="market">market</option>
                            <option value="deal">deal</option>
                          </select>
                        </Field>
                      </div>
                      <div className="md:col-span-2">
                        <Field label="Kind">
                          <select className="field !py-1" disabled={disabled} value={d.kind} onChange={(e) => setDriver(i, { kind: e.target.value as CatalogueDriver['kind'] })}>
                            <option value="relative">relative (%)</option>
                            <option value="absolute">absolute (€/unit)</option>
                          </select>
                        </Field>
                      </div>
                      <div className="md:col-span-2">
                        <Field label="Levels">
                          <input
                            className="field !py-1 font-mono text-xs"
                            disabled={disabled}
                            value={d.levels.join(',')}
                            onChange={(e) => setDriver(i, { levels: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })}
                          />
                        </Field>
                      </div>
                      <div className="md:col-span-12">
                        <Field label="Level labels (optional, one per level)">
                          <input
                            className="field !py-1 text-xs"
                            disabled={disabled}
                            value={(d.levelLabels ?? []).join(', ')}
                            onChange={(e) => {
                              const labels = e.target.value.split(',').map((x) => x.trim())
                              if (labels.some(Boolean)) setDriver(i, { levelLabels: labels })
                              else {
                                const { levelLabels: _drop, ...rest } = value.drivers[i]!
                                onChange({ ...value, drivers: value.drivers.map((x, j) => (j === i ? rest : x)) })
                              }
                            }}
                          />
                        </Field>
                      </div>
                      <div className="md:col-span-12">
                        <Field label="Level labels in German (optional, one per level)">
                          <input
                            className="field !py-1 text-xs"
                            disabled={disabled}
                            value={(d.levelLabelsDe ?? []).join(', ')}
                            onChange={(e) => {
                              const labels = e.target.value.split(',').map((x) => x.trim())
                              if (labels.some(Boolean)) setDriver(i, { levelLabelsDe: labels })
                              else {
                                const { levelLabelsDe: _drop, ...rest } = value.drivers[i]!
                                onChange({ ...value, drivers: value.drivers.map((x, j) => (j === i ? rest : x)) })
                              }
                            }}
                          />
                        </Field>
                      </div>
                      <div className="md:col-span-6">
                        <Field label={`${d.levels[0] ?? 'low'} means`}>
                          <input className="field !py-1 text-xs" disabled={disabled} value={d.anchorLow ?? ''} onChange={(e) => setDriver(i, { anchorLow: e.target.value })} />
                        </Field>
                      </div>
                      <div className="md:col-span-6">
                        <Field label={`${d.levels[d.levels.length - 1] ?? 'high'} means`}>
                          <input className="field !py-1 text-xs" disabled={disabled} value={d.anchorHigh ?? ''} onChange={(e) => setDriver(i, { anchorHigh: e.target.value })} />
                        </Field>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Chip active={!!d.fitted} onClick={disabled ? undefined : () => setDriver(i, { fitted: !d.fitted })} title="Weights come from the calibration app rather than being set centrally">fitted</Chip>
                      <Chip active={!!d.reversed} onClick={disabled ? undefined : () => setDriver(i, { reversed: !d.reversed })} title="The scale reads against its natural magnitude">reversed ⇄</Chip>
                      <input className="field ml-2 max-w-md !py-0.5 text-xs" placeholder="source note (ratings file, who sets it)" disabled={disabled} value={d.source ?? ''} onChange={(e) => setDriver(i, { source: e.target.value })} />
                      <button type="button" className="btn btn-danger ml-auto !py-0.5 text-xs" disabled={disabled} onClick={() => removeDriver(i)}>
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        })
      )}
    </div>
  )
}
